import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePeriod, useGo, useBlock, BlockState, PeriodBar, Tile, Row, SectionTitle, money, pct, formatHours } from './ceoShared';

// Блоки CEO-дашборда. Каждый принимает go(module, list) — клик по цифре/заголовку открывает
// страницу модуля с расшифровкой (см. CeoModule.js), и active — какой список сейчас открыт
// (на странице модуля подсвечиваем соответствующую плитку).

// Блок 1 — алерт разноски. Период не влияет. На дашборде показывается только при наличии
// неразнесённого; на странице модуля «Разноска» (always) — всегда.
export function AlertBlock({ go, active, always }) {
  const state = useBlock('/finance/ceo/alert');
  const navigate = useNavigate();
  if (state.loading || state.error) return always ? <div className="card" style={{ marginBottom: 20 }}><BlockState state={state}>{() => null}</BlockState></div> : null;
  const d = state.data;
  if (!always && d.bankUncategorized + d.requestsWithoutProjectOrArticle === 0) return null;
  return (
    <div className="card" style={{ background: '#fff6dd', borderColor: 'var(--warning)', marginBottom: 20 }}>
      <SectionTitle onClick={always ? undefined : () => go('allocation', 'bank-uncategorized')}>Разноска</SectionTitle>
      <div className="stats-row" style={{ marginBottom: 10 }}>
        <Tile value={d.bankUncategorized} label="Операций из банка без статьи" onClick={() => go('allocation', 'bank-uncategorized')} active={active === 'bank-uncategorized'} />
        <Tile value={d.requestsWithoutProjectOrArticle} label="Заявок без проекта или статьи" onClick={() => go('allocation', 'requests-unallocated')} active={active === 'requests-unallocated'} />
        <Tile value={money(d.unallocatedSum)} label="Сумма неразнесённого" />
        <Tile
          value={d.lastBankSync ? new Date(d.lastBankSync).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
          label="Последняя синхронизация банка"
          hint={d.bankSyncHasError ? 'по части счетов — ошибка синхронизации' : null}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>Пока эти операции не разнесены, P&L считается неточно.</span>
        <div className="toolbar-spacer" />
        <button className="btn-primary btn-sm" onClick={() => navigate('/finance/categorization')}>Перейти к категоризации</button>
      </div>
    </div>
  );
}

export function PnlBlock({ range, go, active }) {
  const state = useBlock('/finance/ceo/pnl', range);
  const arrow = <div style={{ alignSelf: 'center', fontSize: 20, color: 'var(--text-muted)' }}>→</div>;
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <SectionTitle onClick={() => go('pnl', 'pnl-articles')} extra={state.data?.incomplete && <span style={{ color: 'var(--warning)', fontSize: 13, fontWeight: 600 }}>⚠ данные неполные</span>}>
        P&L за период
      </SectionTitle>
      <BlockState state={state}>
        {(d) => (
          <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
            <Tile value={money(d.revenue)} label="Выручка" onClick={() => go('pnl', 'revenue')} active={active === 'revenue'} />
            {arrow}
            <Tile value={money(d.expenses)} label="Расходы" onClick={() => go('pnl', 'expenses')} active={active === 'expenses'} />
            {arrow}
            <Tile value={money(d.margin)} label="Маржа" danger={d.margin < 0} onClick={() => go('pnl', 'pnl-articles')} active={active === 'pnl-articles'} />
            {arrow}
            <Tile value={pct(d.rentability)} label="Рентабельность" danger={d.rentability != null && d.rentability < 0} onClick={() => go('pnl', 'pnl-articles')} />
          </div>
        )}
      </BlockState>
    </div>
  );
}

export function BalancesCard({ go, active, accountId }) {
  const state = useBlock('/finance/ceo/balances');
  return (
    <div className="card" style={{ flex: 1, minWidth: 320 }}>
      <SectionTitle onClick={() => go('money', 'payable')}>Остатки по счетам</SectionTitle>
      <BlockState state={state}>
        {(d) => (
          <>
            {d.accounts.map(a => (
              <Row key={a.id} label={a.name} onClick={() => go('money', 'account-entries', { account_id: a.id })}
                active={active === 'account-entries' && String(accountId) === String(a.id)}>
                {a.error
                  ? <span className="text-muted" title={a.error}>нет данных ⚠</span>
                  : <b>{money(a.amount)}</b>}
              </Row>
            ))}
            <Row label={<b>Итого</b>} last><b>{money(d.total)}</b></Row>
            {d.hasErrors && <div className="text-muted" style={{ fontSize: 12 }}>Без счетов, по которым банк не отдал остаток (наведите на «нет данных», чтобы увидеть причину).</div>}
          </>
        )}
      </BlockState>
    </div>
  );
}

export function DebtsCard({ range, go, active }) {
  const state = useBlock('/finance/ceo/debts', range);
  const red = (n) => (n ? { color: 'var(--danger)' } : undefined);
  return (
    <div className="card" style={{ flex: 1, minWidth: 320 }}>
      <SectionTitle onClick={() => go('money', 'payable')}>Задолженности</SectionTitle>
      <BlockState state={state}>
        {(d) => (
          <>
            <Row label="Кредиторка (к оплате)" onClick={() => go('money', 'payable')} active={active === 'payable'}><b>{d.payable.count} шт. · {money(d.payable.sum)}</b></Row>
            <Row label="Просрочено" onClick={() => go('money', 'overdue')} active={active === 'overdue'}><b style={red(d.overdue.count)}>{d.overdue.count} шт.</b></Row>
            <Row label="Дебиторка (должны нам)" onClick={() => go('money', 'receivable')} active={active === 'receivable'}><b>{money(d.receivable.sum)}</b></Row>
            <Row label="Завершённые без оплаты" onClick={() => go('money', 'completed-unpaid')} active={active === 'completed-unpaid'}><b style={red(d.completedUnpaid.count)}>{d.completedUnpaid.count} шт.</b></Row>
            <Row label="На подписи" onClick={() => go('money', 'pending')} active={active === 'pending'} last><b>{d.pending.count} шт. · {money(d.pending.sum)}</b></Row>
          </>
        )}
      </BlockState>
    </div>
  );
}

export function ProjectsColumn({ range, go, active, threshold, setThreshold }) {
  const params = useMemo(() => ({ ...range, threshold }), [range, threshold]);
  const state = useBlock('/finance/ceo/projects', params);
  return (
    <div className="card" style={{ flex: 1, minWidth: 320 }}>
      <SectionTitle onClick={() => go('projects', 'projects-in-progress')}>Проекты</SectionTitle>
      <BlockState state={state}>
        {(d) => (
          <>
            <div className="stats-row">
              <Tile value={d.inProgress} label="В работе" onClick={() => go('projects', 'projects-in-progress')} active={active === 'projects-in-progress'} />
              <Tile value={d.closedInPeriod} label="Закрыто в периоде" onClick={() => go('projects', 'projects-closed')} active={active === 'projects-closed'} />
              <Tile value={d.onHold} label="Приостановлено" onClick={() => go('projects', 'projects-on-hold')} active={active === 'projects-on-hold'} />
            </div>
            <div className="card" style={{ background: 'var(--bg)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <b style={{ cursor: 'pointer' }} onClick={() => go('projects', 'projects-low-margin', { threshold })}>Маржа ниже</b>
                <input type="number" min="1" max="100" value={threshold} style={{ width: 70 }}
                  onChange={e => setThreshold(Math.min(100, Math.max(1, Number(e.target.value) || 50)))} />
                <b>%</b>
                <span className="text-muted" style={{ cursor: 'pointer' }} onClick={() => go('projects', 'projects-low-margin', { threshold })}>→</span>
              </div>
              {d.lowMargin.length === 0 ? <div className="text-muted">Таких проектов нет</div> : d.lowMargin.map((p, i) => (
                <Row key={p.id} label={p.name} last={i === d.lowMargin.length - 1} onClick={() => go('projects', 'projects-low-margin', { threshold })}>
                  <b style={{ color: 'var(--danger)', whiteSpace: 'nowrap' }}>{p.marginPercent.toFixed(0)}%</b>
                </Row>
              ))}
            </div>
          </>
        )}
      </BlockState>
    </div>
  );
}

export function SalesColumn({ range, go, active }) {
  const state = useBlock('/finance/ceo/sales', range);
  return (
    <div className="card" style={{ flex: 1, minWidth: 320 }}>
      <SectionTitle onClick={() => go('sales', 'deals-new')} extra={state.data?.updatedAt && <span className="text-muted" style={{ fontSize: 12 }}>обновлено {new Date(state.data.updatedAt).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>}>
        Продажи (AmoCRM)
      </SectionTitle>
      <BlockState state={state}>
        {(d) => !d.available ? <div className="text-muted">Данные AmoCRM ещё не загружены</div> : (
          <>
            <div className="stats-row">
              <Tile value={d.newDeals.count} label="Новых сделок за период" hint={money(d.newDeals.sum)} onClick={() => go('sales', 'deals-new')} active={active === 'deals-new'} />
              <Tile value={d.openDeals.count} label="Открытых сделок сейчас" hint={money(d.openDeals.sum)} onClick={() => go('sales', 'deals-open')} active={active === 'deals-open'} />
            </div>
            <div className="card" style={{ background: 'var(--bg)' }}>
              <b style={{ display: 'block', marginBottom: 8 }}>Звонки</b>
              <Row label="Медиана времени до первого звонка" onClick={() => go('sales', 'deals-first-call')} active={active === 'deals-first-call'}><b>{formatHours(d.calls.medianHours)}</b></Row>
              <Row label="Заявок без ни одного звонка" onClick={() => go('sales', 'deals-no-call')} active={active === 'deals-no-call'}><b style={d.calls.withoutCall ? { color: 'var(--danger)' } : undefined}>{d.calls.withoutCall}</b></Row>
              <Row label="Конверсия в сделку" onClick={() => go('sales', 'deals-new')} last><b>{pct(d.calls.conversionPercent)}</b></Row>
            </div>
          </>
        )}
      </BlockState>
    </div>
  );
}

export function OperationsBlock({ range, go, active }) {
  const state = useBlock('/finance/ceo/operations', range);
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <SectionTitle onClick={() => go('operations', 'queue')}>Операционка</SectionTitle>
      <BlockState state={state}>
        {(d) => (
          <div style={{ display: 'flex', gap: 10 }}>
            <Tile value={d.queue.count} label="Очередь на оплату" hint={money(d.queue.sum)} onClick={() => go('operations', 'queue')} active={active === 'queue'} />
            <Tile value={d.overdue.count} label="Просрочено" danger={d.overdue.count > 0} onClick={() => go('operations', 'overdue')} active={active === 'overdue'} />
            <Tile value={d.bankUncategorized.count} label="Неразмечено в банке" danger={d.bankUncategorized.count > 0} onClick={() => go('operations', 'bank-uncategorized')} active={active === 'bank-uncategorized'} />
            <Tile value={d.designers.inWork} label="Дизайнеры в работе" hint={`сдано в периоде: ${d.designers.doneInPeriod}`} onClick={() => go('operations', 'designers-in-work')} active={active === 'designers-in-work' || active === 'designers-done'} />
          </div>
        )}
      </BlockState>
    </div>
  );
}

export default function CeoDashboard() {
  const period = usePeriod();
  const go = useGo(period);
  const [threshold, setThreshold] = useState(50);
  const range = period.range;

  return (
    <div>
      <PeriodBar period={period} />
      <AlertBlock go={go} />
      {range && (
        <>
          <PnlBlock range={range} go={go} />
          <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
            <BalancesCard go={go} />
            <DebtsCard range={range} go={go} />
          </div>
          <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <ProjectsColumn range={range} go={go} threshold={threshold} setThreshold={setThreshold} />
            <SalesColumn range={range} go={go} />
          </div>
          <OperationsBlock range={range} go={go} />
        </>
      )}
    </div>
  );
}
